import { LitElement, html, unsafeCSS } from "lit-element"
import { classMap } from "lit-html/directives/class-map"
import { stop } from "../utils"
import commonStyle from "../common.css"
import style from "./dialog.css"
import TabTrap from "../tabtrap"

/**
 * ダイアログコンポーネント
 */
export default class extends LitElement {
	static styles = [unsafeCSS(style), unsafeCSS(commonStyle)]
	static get properties () {
		return {
			title: { type: String, attribute: "dialog-title" },
			persistent: { type: Boolean },
			fullScreen: { type: Boolean, attribute: "full-screen" },
			noCloseIcon: { type: Boolean, attribute: "no-close-icon" },
			noHeader: { type: Boolean, attribute: "no-header" },
			absolute: { type: Boolean },
			hasActions: { type: Boolean, attribute: "has-actions" }
		}
	}

	/**
	 * @type {HTMLElement}
	 */
	#modalEl = null

	/**
	 * @type {TabTrap}
	 */
	#tabtrap = null

	constructor () {
		super()
		this.title = ""
		this.fullScreen = false
		this.noCloseIcon = false
		this.noHeader = false
		this.hasActions = false
	}

	render () {
		return html`
		<bui-modal ?persistent=${this.persistent} @modal-beforehide="${this.#beforeClose}" ?absolute=${this.absolute}>
			<div class=${this.#dialogClass} @dialog-beforeclose="${stop}">
				<!-- ダイアログヘッダ -->
				${this.noHeader ? "" : html`
					<header class="dialog-header">
						${this.noCloseIcon ? "" : html`
							<bui-icon
								ripple
								large
								class="icon icon-button hl hide-on-pc"
								icon="fas fa-times"
								@click="${this.close}"
							>
							</bui-icon>`}
						<span class="dialog-title">${this.title}</span>
						<div style="flex:1;"></div>
						<div class="dialog-header-right">
							<slot name="header-actions" class="dialog-header-actions"></slot>
							${this.noCloseIcon ? "" : html`
								<bui-icon
									ripple
									class="close-icon-right icon icon-button hl hide-on-sp"
									icon="fas fa-times"
									@click="${this.close}"
								></bui-icon>`}
						</div>
					</header>
				`}
				<!-- ダイアログボディ -->
				<main class="dialog-body">
					<slot></slot>
				</main>
				<!-- ダイアログアクション -->
				${this.hasActions ? html`<div class="dialog-actions">
					<slot name="actions">
					</slot>
				</div>` : ""}
			</div>
		</bui-modal>
		`
	}

	get #dialogClass () {
		return classMap({
			dialog: true,
			"overlay-shadow": true,
			"is-full-screen": this.fullScreen,
			"is-open": this.isOpen
		})
	}

	/**
	 * @return {boolean} 表示中ならtrue
	 */
	get isOpen () {
		return this.#modalEl ? this.#modalEl.isShow : false
	}

	firstUpdated () {
		this.#modalEl = this.shadowRoot.querySelector("bui-modal")
	}

	open () {
		this.#modalEl.show()
		return this.requestUpdate().then(() => {
			this.#tabtrap = new TabTrap(this.shadowRoot.host)
			this.#tabtrap.focusDummy()
		})
	}

	close () {
		this.#modalEl.hide(true)
		this.#tabtrap = null
	}

	/**
	 * モーダルからクローズイベントが発火された場合、dialog-beforecloseイベントを発火し、
	 * キャンセルされた場合はモーダルのクローズもキャンセルする
	 * @param {Event} e
	 */
	#beforeClose (e) {
		const isCancelled = !this.dispatchEvent(new CustomEvent("dialog-beforeclose", { cancelable: true }))
		if (isCancelled) {
			e.preventDefault()
		} else {
			this.dispatchEvent(new CustomEvent("dialog-close"))
		}
	}
}
